import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faRobot,
  faLightbulb,
  faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

export interface AIAnalysis {
  summary: string;
  insights: string[];
  recommendations: string[];
  calculationVerification?: string;
  confidence: number;
}

interface AIInsightsPanelProps {
  analysis: AIAnalysis;
  onClose: () => void;
}

export const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({ analysis, onClose }) => {
  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-xl p-4 shadow-lg">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon={faRobot} className="text-blue-600 text-sm" />
          <h3 className="text-xs font-semibold text-gray-900">AI-Powered Cost Analysis</h3>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700">
            {Math.round(analysis.confidence * 100)}% Confidence
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xs"
        >
          ✕
        </button>
      </div>

      {/* Summary */}
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-gray-900 mb-2">Summary</h4>
        <p className="text-xs text-gray-700 leading-relaxed bg-white rounded-lg p-3 border border-blue-200">
          {analysis.summary}
        </p>
      </div>

      {/* Insights */}
      {analysis.insights && analysis.insights.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon icon={faLightbulb} className="text-yellow-600 mr-2 text-xs" />
            Key Insights ({analysis.insights.length})
          </h4>
          <ul className="space-y-2">
            {analysis.insights.map((insight, index) => (
              <li key={index} className="text-xs text-gray-700 bg-white rounded-lg p-2 border border-yellow-200 flex items-start">
                <span className="text-yellow-600 mr-2 font-bold">•</span>
                <span>{insight}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Recommendations */}
      {analysis.recommendations && analysis.recommendations.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon icon={faLightbulb} className="text-green-600 mr-2 text-xs" />
            Recommendations ({analysis.recommendations.length})
          </h4>
          <ul className="space-y-2">
            {analysis.recommendations.map((recommendation, index) => (
              <li key={index} className="text-xs text-gray-700 bg-white rounded-lg p-2 border border-green-200 flex items-start">
                <span className="text-green-600 mr-2 font-bold">→</span>
                <span>{recommendation}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Calculation Verification */}
      {analysis.calculationVerification && (
        <div className="bg-white rounded-lg p-3 border border-green-200">
          <h4 className="text-xs font-semibold text-gray-900 mb-2 flex items-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-green-600 mr-2 text-xs" />
            Calculation Verification
          </h4>
          <p className="text-xs text-gray-700">{analysis.calculationVerification}</p>
        </div>
      )}
    </div>
  );
};

interface AIInsightsButtonProps {
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

export const AIInsightsButton: React.FC<AIInsightsButtonProps> = ({
  onClick,
  loading = false,
  disabled = false
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center space-x-2 ${loading ? 'animate-pulse' : ''}`}
    >
      <FontAwesomeIcon icon={faRobot} className="text-xs" />
      <span>{loading ? 'Analyzing...' : 'AI Insights'}</span>
    </button>
  );
};

/**
 * Hook to fetch AI analysis for a specific tab
 */
export const useAIAnalysis = (tabName: string) => {
  const [aiAnalysis, setAiAnalysis] = React.useState<AIAnalysis | null>(null);
  const [showAIInsights, setShowAIInsights] = React.useState(false);
  const [analyzing, setAnalyzing] = React.useState(false);

  const fetchAnalysis = async (tabData: any) => {
    setAnalyzing(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const response = await fetch(`${baseUrl}/api/cost/tab-analysis/${tabName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ tabData })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch AI analysis');
      }

      const result = await response.json();
      setAiAnalysis(result.analysis);
      setShowAIInsights(true);
    } catch (error) {
      console.error('Error fetching AI analysis:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  return {
    aiAnalysis,
    showAIInsights,
    analyzing,
    fetchAnalysis,
    setShowAIInsights
  };
};
